import {compact} from 'underscore';

import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {assert, assertAndContinue} from 'Asserts';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import {SampleDetails} from 'constants/SampleConstants';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {ClientId, FromServerId, ToServerId} from 'notebook/ELNConstants';
import * as ContainerSourceTypeCustomEval from 'notebook/integrations/samples/ContainerSourceTypeCustomEval';
import {
  getErrorsForInvalidParentStorableCapacityInNonFinalizedRows,
  getParentStorablesByRowId,
  getUnfinalizedParentStorablePrimarySample,
} from 'notebook/integrations/samples/InventoryCapacityValidationFunctions';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {TransferQuantityCustomEval} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableQuantityCustomEval';
import * as ParentStorableCustomEval from 'notebook/integrations/samples/transfer-table-columns/ParentStorableCustomEval';
import {SampleControlRolesCustomEval} from 'notebook/integrations/samples/transfer-table-columns/SampleControlRolesCustomEval';
import * as SchemaTagsCustomEval from 'notebook/integrations/samples/transfer-table-columns/SchemaTagsCustomEval';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import getSampleDetailsFromCell from 'notebook/sample-propagation/sample-collection/getSampleDetailsFromCell';
import getSampleId from 'notebook/sample-propagation/util/getSampleId';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {createRequestActionFunction} from 'notebook/structured-tables/action-functions/createRequestActionFunction';
import {fillNextAvailablePositionsActionFunction} from 'notebook/structured-tables/action-functions/fillNextAvailablePositionsButtonActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {transferActionFunction} from 'notebook/structured-tables/action-functions/transferActionFunction';
import {updateTransferTableSettingsActionFunction} from 'notebook/structured-tables/action-functions/updateTransferTableSettingsActionFunction';
import {getColumnDefinitionIndex} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {filterOutColumnTypesWithColumnGroups} from 'notebook/structured-tables/columns/filterOutColumnTypes';
import {
  isContainersStructuredTableColumn,
  isPartiesStructuredTableColumn,
  isSampleRestrictionStatusStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import {StructuredTableFolderOption} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {TransferStructuredTableRowUpdater} from 'notebook/structured-tables/row-updaters/TransferStructuredTableRowUpdater';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {getColumnAdapter} from 'notebook/structured-tables/StructuredTableColumnAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  EvalTableView,
  GetCustomEvalArgsOptions,
  PartiesStructuredTableColumn,
  PrimarySample,
  PrimarySampleLookupType,
  SampleRestrictionStatusStructuredTableColumn,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableColumnSummary,
  StructuredTableDomainGraphType,
  StructuredTableMetadata,
  StructuredTableRow,
  StructuredTableSchema,
  TableValidationFn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CustomEvalFnAndType, CustomEvalType} from 'notebook/tables/constants/CustomEvalConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType, USER_CREATED_COLUMN_TYPES} from 'notebook/tables/constants/TableColumnConstants';
import {CellIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {
  formatTypedValue,
  getBioentityInfoFromTypedValue,
  getStorableInfoFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import notifications from 'notifications';
import {createProcessActionFunction} from 'processes/tables/action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from 'processes/tables/action-functions/createRequestV2SubmissionActionFunction';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {InvalidPositionError, parseGridPosition} from 'samples/SampleUtil';
import {ContainerSchema, PlateSchema} from 'samples/storables/StorableSchemaConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import submitTransferRowsActionFunction from './submitTransferRowsActionFunction';
import * as BioentitySourceTypeCustomEval from './transfer-table-columns/BioentitySourceTypeCustomEval';
import * as ContainerDestinationTypeCustomEval from './transfer-table-columns/ContainerDestinationTypeCustomEval';
import * as DestinationMatrixPlateCustomEval from './transfer-table-columns/DestinationMatrixPlateCustomEval';
import {destinationPlateCustomEval} from './transfer-table-columns/DestinationPlateCustomEval';
import * as DestinationPositionCustomEval from './transfer-table-columns/DestinationPositionCustomEval';
import * as PlateSourceTypeCustomEval from './transfer-table-columns/PlateSourceTypeCustomEval';
import {extractContainerSchemaAndColumnGroup} from './TransferStructuredTableSchemasUtil';
import {
  getErrorsForDuplicateBarcodesInNonFinalizedRows,
  getErrorsForDuplicateDestinationChipsInNonFinalizedRows,
  getErrorsForDuplicatePositionsInNonFinalizedRows,
} from './TransferTableValidationFunctions';

// Keep in sync with benchling/notebook/structured_tables/constants/transfer_tables.py
export type TransferStructuredTableMetadata =
  | TransferStructuredTableMetadataForCreate
  | TransferStructuredTableMetadataForFill;

/**
 * Keep in sync with benchling/notebook/structured_tables/constants/transfer_tables.py
 */
export interface TransferStructuredTableMetadataForCreate {
  mode: InventoryStructuredTableMode.CREATE;
  sourceType: TransferStructuredTableSourceType;
  destinationType:
    | TransferStructuredTableDestinationType.BOX
    | TransferStructuredTableDestinationType.CONTAINER
    | TransferStructuredTableDestinationType.MATRIX_PLATE;
  barcodingStrategy: TransferStructuredTableBarcodingStrategy;
  // TODO(BNCH-14148) - have this computed at run-time
  // This should be true when the destination type is a BOX
  parentStorableIsGrid: boolean;
  canChangeUnits: boolean;
  searchAllSchemas: boolean;
  includeSampleRoles?: boolean;
  folderOption?: StructuredTableFolderOption;
  /* If folderOption is SPECIFY_FOLDER, then folderId designates the selected folder to create items in.
   * Otherwise it will be undefined. */
  folderId?: string;
}

/**
 * Keep in sync with benchling/notebook/structured_tables/constants/transfer_tables.py
 */
export interface TransferStructuredTableMetadataForFill {
  mode: InventoryStructuredTableMode.FILL;
  sourceType: TransferStructuredTableSourceType;
  destinationType:
    | TransferStructuredTableDestinationType.PLATE
    | TransferStructuredTableDestinationType.CONTAINER;
  barcodingStrategy: null;
  // TODO(BNCH-14148) - have this computed at run-time
  // This should be true when the destination type is a BOX or PLATE
  parentStorableIsGrid: boolean;
  canChangeUnits: boolean;
  searchAllSchemas: boolean;
  includeSampleRoles?: boolean;
  folderOption?: StructuredTableFolderOption;
  folderId?: string;
}

// The source type specifies where the sample is transferred from.
// KEEP IN SYNC WITH DG ENUM UNTIL IT REPLACES THIS.
export enum TransferStructuredTableSourceType {
  BIOENTITY = 'BIOENTITY', // Produce new sample material from nothing
  CONTAINER = 'CONTAINER', // Take existing sample material from a container
  PLATE = 'PLATE', // Take existing sample material from a well referenced by its plate and position
}

// The destination type specifies where the sample is transferred to.
// KEEP IN SYNC WITH DG ENUM UNTIL IT REPLACES THIS.
export enum TransferStructuredTableDestinationType {
  PLATE = 'PLATE', // Deposit sample material into a well referenced by its plate and position
  MATRIX_PLATE = 'MATRIX_PLATE', // Deposit sample material into a container referenced by its parent matrix plate and position
  BOX = 'BOX', // Deposit sample material into a container referenced by its parent box and position
  CONTAINER = 'CONTAINER', // Deposit sample material into a container
}

// KEEP IN SYNC WITH DG ENUM UNTIL IT REPLACES THIS.
export enum TransferStructuredTableBarcodingStrategy {
  AUTOGENERATE = 'AUTOGENERATE', // Generate barcodes for newly created containers
  CUSTOM = 'CUSTOM', // Require the user to specify barcodes
}

export interface TransferStructuredTableRowMetadata extends RowMetadata {}

export type GenericTransferStructuredTable<TIds> = BaseStructuredTable<
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata,
  TIds
>;

export type TransferStructuredTable = GenericTransferStructuredTable<ClientId>;
export type FromServerTransferStructuredTable = GenericTransferStructuredTable<FromServerId>;
export type ToServerTransferStructuredTable = GenericTransferStructuredTable<ToServerId>;

export type TransferStructuredTableRow = StructuredTableRow<TransferStructuredTableRowMetadata>;

// Keep in Sync with benchling/notebook/structured_tables/inventory_tables.py
export const SOURCE_BIOENTITY_COLUMN_NAME = 'Source Entity';
export const SOURCE_CONTAINER_COLUMN_NAME = 'Source Container';
export const SOURCE_PLATE_COLUMN_NAME = 'Source Plate';
export const SOURCE_WELL_COLUMN_NAME = 'Source Well';

export const DESTINATION_BOX_COLUMN_NAME = 'Destination Box';
export const DESTINATION_MATRIX_PLATE_COLUMN_NAME = 'Destination Matrix Plate';
export const DESTINATION_PLATE_COLUMN_NAME = 'Destination Plate';
export const DESTINATION_LOCATION_COLUMN_NAME = 'Destination Location';
export const DESTINATION_POSITION_COLUMN_NAME = 'Destination Position';
export const DESTINATION_WELL_COLUMN_NAME = 'Destination Well';
export const DESTINATION_CONTAINER_COLUMN_NAME = 'Destination Container';

export const DISPLAY_NAME_CREATE_CONTAINERS = 'Create new containers';
export const DISPLAY_NAME_FILL_PLATES = 'Fill plates';
export const DISPLAY_NAME_FILL_CONTAINERS = 'Fill containers';

export const BARCODE_ALREADY_ENTERED_ERROR = 'Barcode has already been entered';
export const POSITION_ALREADY_ENTERED_ERROR = 'Position has already been entered';
export const CONTAINER_ALREADY_ENTERED_ERROR = 'Container has already been entered';

interface ColumnCustomEvalInfo extends CustomEvalFnAndType<{
  tableMetadata: StructuredTableMetadata & TransferStructuredTableMetadata;
  rowMetadata: TransferStructuredTableRowMetadata;
}> {
  getColumnIndexesOfDependencies(columns: ReadonlyArray<StructuredTableColumn>): Array<number>;
}

export class TransferStructuredTableAdapter extends GenericStructuredTableAdapter<
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata
> {
  getDisplayName(structuredTable: TransferStructuredTable): string {
    if (structuredTable.metadata.mode === InventoryStructuredTableMode.CREATE) {
      return DISPLAY_NAME_CREATE_CONTAINERS;
    }
    if (structuredTable.metadata.destinationType === TransferStructuredTableDestinationType.PLATE) {
      return DISPLAY_NAME_FILL_PLATES;
    }
    return DISPLAY_NAME_FILL_CONTAINERS;
  }

  /**
   * Tag Value and Lookup columns can always be edited, but Quantity, Concentration, Parties (sample owners
   * & restricted sample users) & Restriction status columns can only be edited before the table is submitted
   *
   * @param structuredTable - the structured table containing the columns
   * @param column - the column to determine if is editable on this table
   * @returns if the table allows that column to be edited
   */
  override isColumnUserConfigurable(
    structuredTable: TransferStructuredTable,
    column: StructuredTableColumn | null
  ): boolean {
    const editableTypes = [TableColumnType.TAG_VALUE, ...USER_CREATED_COLUMN_TYPES];
    if (!structuredTable.rows.some((row) => row.isFinalized)) {
      editableTypes.push(
        TableColumnType.QUANTITY,
        TableColumnType.CONCENTRATION,
        TableColumnType.PARTIES,
        TableColumnType.SAMPLE_RESTRICTION_STATUS
      );
    }
    const type = column?.columnType;
    if (type == null) {
      // If we don't know what kind of column this is, we'll just be safe and say it's not editable.
      // This _shouldn't_ occur for StructuredTables but there might be some weird historical detail that
      // we're missing.
      return false;
    }
    return editableTypes.includes(type) || (getColumnAdapter(type)?.isUserConfigurable ?? false);
  }

  getBlankRowMetadata(metadata: TransferStructuredTableMetadata): TransferStructuredTableRowMetadata {
    return {};
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: TransferStructuredTableMetadata;
  }): StructuredTableColumn {
    // Check that user has permission to access schemas
    const containerSchema = extractContainerSchemaAndColumnGroup(schemas, TransferTableColumnGroup.TARGET);
    if (!containerSchema) {
      return column;
    }
    if (isContainersStructuredTableColumn(column) && column.columnGroup === TransferTableColumnGroup.TARGET) {
      // TODO(BNCH-18968): make the backend return just the container schema ID (without any permissions check),
      // so that we can construct the same resolution queries no matter who is accessing the table
      return {
        ...column,
        frontEndMetadata: {
          containerSchemaId: containerSchema.id,
          containerSchemaName: containerSchema.name,
        },
      };
    } else if (
      isPartiesStructuredTableColumn(column) ||
      isSampleRestrictionStatusStructuredTableColumn(column)
    ) {
      return {
        ...column,
        frontEndMetadata: {
          storableSchemaId: containerSchema.id,
        },
      };
    }

    return column;
  }

  override getActionFunctions(
    tableMetadata: TransferStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<TransferStructuredTableMetadata, {}> {
    const inherited = super.getActionFunctions(tableMetadata, schemas);
    return {
      primary: [transferActionFunction],
      secondary: [
        // TODO: Figure out if this use of ! is safe.
        ...inherited.secondary!,
        createRequestV2SubmissionActionFunction,
        createProcessActionFunction,
        createRequestActionFunction,
        // Can only fill positions for plates and boxes
        ...(tableMetadata.destinationType !== TransferStructuredTableDestinationType.CONTAINER
          ? [fillNextAvailablePositionsActionFunction]
          : []),
        addToWorklistActionFunction,
        updateTransferTableSettingsActionFunction,
      ],
      // TODO: Figure out if this use of ! is safe.
      overflow: [...inherited.overflow!, printBarcodesActionFunction],
      contextMenu: [submitTransferRowsActionFunction],
      footer: inherited.footer ?? [],
    };
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: TransferStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return filterOutColumnTypesWithColumnGroups(
      super.getInitialColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      }),
      metadata.includeSampleRoles ? [] : [TableColumnType.PARTIES, TableColumnType.SAMPLE_RESTRICTION_STATUS],
      {
        [TableColumnType.PARTIES]: [
          StructuredTableColumnGroups.SAMPLE_OWNERS,
          StructuredTableColumnGroups.AUTHORIZED_USERS,
        ],
        [TableColumnType.SAMPLE_RESTRICTION_STATUS]: [TransferTableColumnGroup.TARGET],
      }
    );
  }

  override getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: TransferStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const sourceColumns = this.getSourceColumns(metadata);
    const destinationColumns = this.getDestinationColumns(metadata);
    const containerQuantityColumn = this.getContainerQuantityColumn();
    const containerConcentrationColumn = this.getConcentrationColumn();
    const sampleControlRolesColumns = this.getSampleControlRolesColumns(metadata);

    const containerSchemaAndColumnGroup = schemas && extractContainerSchemaAndColumnGroup(schemas);
    const destinationSchemaTagColumns = super.getAllSupportedColumns({
      entryId,
      metadata,
      schemas: containerSchemaAndColumnGroup ? [containerSchemaAndColumnGroup] : [],
      selectorsById,
      structuredTableInsertionContext,
    });

    return [
      ...sourceColumns,
      ...destinationColumns,
      ...containerQuantityColumn,
      ...containerConcentrationColumn,
      ...sampleControlRolesColumns,
      ...destinationSchemaTagColumns,
    ];
  }

  // This functionality is mirrored in benchling/notebook/structured_tables/instantiators/transfer_table_instantiator.py
  // Make sure any changes here are reflected there if relevant to programmatically generated tables.
  getSourceColumns(metadata: TransferStructuredTableMetadata): Array<StructuredTableColumn> {
    switch (metadata.sourceType) {
      case TransferStructuredTableSourceType.BIOENTITY:
        return [
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.EXISTING_BIOENTITY,
              name: SOURCE_BIOENTITY_COLUMN_NAME,
              columnGroup: TransferTableColumnGroup.SOURCE,
            }),
            isReadOnly: false,
            isRequired: true,
          },
        ];
      case TransferStructuredTableSourceType.CONTAINER:
        return [
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.CONTAINERS,
              name: SOURCE_CONTAINER_COLUMN_NAME,
              columnGroup: TransferTableColumnGroup.SOURCE,
            }),
            isReadOnly: false,
            isRequired: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.EXISTING_BIOENTITY,
              name: SOURCE_BIOENTITY_COLUMN_NAME,
              columnGroup: TransferTableColumnGroup.SOURCE,
            }),
            isReadOnly: true,
          },
        ];
      case TransferStructuredTableSourceType.PLATE:
        return [
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.PLATE,
              name: SOURCE_PLATE_COLUMN_NAME,
              columnGroup: TransferTableColumnGroup.SOURCE,
            }),
            isReadOnly: false,
            isRequired: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.POSITION,
              name: SOURCE_WELL_COLUMN_NAME,
              columnGroup: TransferTableColumnGroup.SOURCE,
            }),
            isReadOnly: false,
            isRequired: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.EXISTING_BIOENTITY,
              name: SOURCE_BIOENTITY_COLUMN_NAME,
              columnGroup: TransferTableColumnGroup.SOURCE,
            }),
            isReadOnly: true,
          },
        ];
      default:
        assertAndContinue(false, `Unexpected sourceType: ${assertNever(metadata.sourceType)}`);
        return [];
    }
  }

  // This functionality is mirrored in benchling/notebook/structured_tables/instantiators/transfer_table_instantiator.py
  // Make sure any changes here are reflected there if relevant to programmatically generated tables.
  getDestinationColumns(metadata: TransferStructuredTableMetadata): Array<StructuredTableColumn> {
    const containersColumn = [
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.CONTAINERS,
          name: DESTINATION_CONTAINER_COLUMN_NAME,
          columnGroup: TransferTableColumnGroup.TARGET,
        }),
        isReadOnly: metadata.barcodingStrategy === TransferStructuredTableBarcodingStrategy.AUTOGENERATE,
        isRequired:
          metadata.barcodingStrategy == null ||
          metadata.barcodingStrategy === TransferStructuredTableBarcodingStrategy.CUSTOM,
      },
    ];
    switch (metadata.destinationType) {
      case TransferStructuredTableDestinationType.BOX:
        return [
          makeStructuredTableColumn({
            columnType: TableColumnType.BOX,
            name: DESTINATION_BOX_COLUMN_NAME,
            columnGroup: TransferTableColumnGroup.TARGET,
            isRequired: true,
          }),
          makeStructuredTableColumn({
            columnType: TableColumnType.POSITION,
            name: DESTINATION_POSITION_COLUMN_NAME,
            columnGroup: TransferTableColumnGroup.TARGET,
            isRequired: true,
          }),
          ...containersColumn,
        ];
      case TransferStructuredTableDestinationType.MATRIX_PLATE:
        return [
          makeStructuredTableColumn({
            columnType: TableColumnType.PLATE,
            name: DESTINATION_MATRIX_PLATE_COLUMN_NAME,
            columnGroup: TransferTableColumnGroup.TARGET,
            isRequired: true,
          }),
          makeStructuredTableColumn({
            columnType: TableColumnType.POSITION,
            name: DESTINATION_POSITION_COLUMN_NAME,
            columnGroup: TransferTableColumnGroup.TARGET,
            isRequired: true,
          }),
          ...containersColumn,
        ];
      case TransferStructuredTableDestinationType.PLATE:
        return [
          makeStructuredTableColumn({
            columnType: TableColumnType.PLATE,
            name: DESTINATION_PLATE_COLUMN_NAME,
            columnGroup: TransferTableColumnGroup.TARGET,
            isRequired: true,
          }),
          makeStructuredTableColumn({
            columnType: TableColumnType.POSITION,
            name: DESTINATION_WELL_COLUMN_NAME,
            columnGroup: TransferTableColumnGroup.TARGET,
            isRequired: true,
          }),
        ];
      case TransferStructuredTableDestinationType.CONTAINER:
        return metadata.mode === InventoryStructuredTableMode.CREATE
          ? [
              makeStructuredTableColumn({
                columnType: TableColumnType.PARENT_STORABLE,
                name: DESTINATION_LOCATION_COLUMN_NAME,
                columnGroup: TransferTableColumnGroup.TARGET,
                isRequired: true,
              }),
              ...containersColumn,
            ]
          : containersColumn;
      default:
        assertAndContinue(false, `Unexpected destinationType: ${assertNever(metadata).destinationType}`);
        return [];
    }
  }

  getContainerQuantityColumn(): Array<StructuredTableColumn> {
    return [
      makeStructuredTableColumn({
        columnType: TableColumnType.QUANTITY,
        name: 'Quantity',
        columnGroup: TransferTableColumnGroup.TARGET,
      }),
    ];
  }

  getConcentrationColumn(): Array<StructuredTableColumn> {
    return [
      makeStructuredTableColumn({
        columnType: TableColumnType.CONCENTRATION,
        name: 'Concentration',
        columnGroup: TransferTableColumnGroup.TARGET,
      }),
    ];
  }

  getSampleControlRolesColumns(metadata: TransferStructuredTableMetadata): Array<StructuredTableColumn> {
    if (!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)) {
      return [];
    }
    if (metadata.destinationType === TransferStructuredTableDestinationType.PLATE) {
      assertAndContinue(
        !metadata.includeSampleRoles,
        'Include sample roles should not be true for plate destination type'
      );
      return [];
    }
    const ownerColumn = makeStructuredTableColumn({
      columnType: TableColumnType.PARTIES,
      name: 'Sample owners',
      columnGroup: StructuredTableColumnGroups.SAMPLE_OWNERS,
    });
    const authUserColumn = makeStructuredTableColumn({
      columnType: TableColumnType.PARTIES,
      name: getRestrictedSampleUsersHeaderText(),
      columnGroup: StructuredTableColumnGroups.AUTHORIZED_USERS,
    });
    if (hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)) {
      const statusColumn = makeStructuredTableColumn({
        columnType: TableColumnType.SAMPLE_RESTRICTION_STATUS,
        name: 'Restriction status',
        columnGroup: TransferTableColumnGroup.TARGET,
      });
      return [statusColumn, authUserColumn, ownerColumn];
    } else {
      return [authUserColumn, ownerColumn];
    }
  }

  /**
   * Transfer tables come in many different configurations, each with their own needs for customEval.
   * For the Source columns, customEval depends primarily on the table's source type.
   *
   * This method defines, for each source type, how each Source column should behave.
   * For each source type and each column, return the customEval function and dependencies.
   */
  private getSourceColumnCustomEvalInfo(
    tableMetadata: TransferStructuredTableMetadata,
    column: StructuredTableColumn
  ): ColumnCustomEvalInfo | null {
    switch (tableMetadata.sourceType) {
      case TransferStructuredTableSourceType.BIOENTITY:
        if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
          return {
            customEvalFn: BioentitySourceTypeCustomEval.sourceEntityCustomEval,
            customEvalType: CustomEvalType.DEFAULT,
            getColumnIndexesOfDependencies: () => [],
          };
        }
        return null;

      case TransferStructuredTableSourceType.CONTAINER:
        if (column.columnType === TableColumnType.CONTAINERS) {
          return {
            customEvalFn: ContainerSourceTypeCustomEval.sourceContainerCustomEval,
            customEvalType: CustomEvalType.DEFAULT,
            getColumnIndexesOfDependencies: () => [],
          };
        }
        if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
          return {
            customEvalFn: ContainerSourceTypeCustomEval.getSourceEntityCustomEval({
              lockIfContainerValIsEmpty: true,
            }),
            customEvalType: CustomEvalType.DEFAULT,
            getColumnIndexesOfDependencies: (columns) => [
              columns.findIndex(
                ({columnGroup, columnType}) =>
                  columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.CONTAINERS
              ),
            ],
          };
        }
        return null;
      case TransferStructuredTableSourceType.PLATE:
        if (column.columnType === TableColumnType.PLATE) {
          // The Source Plate column doesn't need customEval to work.
          // The only thing missing is that inaccessible plates should show red dog ears.
          // But that should be fixed more generally [TODO(BNCH-15696)], not just for this column.
          return null;
        }
        if (column.columnType === TableColumnType.POSITION) {
          return {
            customEvalFn: PlateSourceTypeCustomEval.getSourcePositionCustomEval(),
            customEvalType: CustomEvalType.DEFAULT,
            getColumnIndexesOfDependencies: (columns) => [
              columns.findIndex(
                ({columnGroup, columnType}) =>
                  columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.PLATE
              ),
            ],
          };
        }
        if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
          return {
            customEvalFn: PlateSourceTypeCustomEval.getSourceEntityCustomEval(),
            customEvalType: CustomEvalType.DEFAULT,
            getColumnIndexesOfDependencies: (columns) => [
              columns.findIndex(
                ({columnGroup, columnType}) =>
                  columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.PLATE
              ),
              columns.findIndex(
                ({columnGroup, columnType}) =>
                  columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.POSITION
              ),
            ],
          };
        }

        return null;
      default:
        assertAndContinue(false, `Unexpected source type ${tableMetadata.sourceType}`);
        return null;
    }
  }

  private getDestinationColumnCustomEvalInfo(
    tableMetadata: TransferStructuredTableMetadata,
    column: StructuredTableColumn
  ): ColumnCustomEvalInfo | null {
    if (tableMetadata.mode === InventoryStructuredTableMode.CREATE) {
      if (
        tableMetadata.destinationType === TransferStructuredTableDestinationType.MATRIX_PLATE &&
        column.columnType === TableColumnType.PLATE
      ) {
        return {
          customEvalFn: DestinationMatrixPlateCustomEval.destinationMatrixPlateCustomEval,
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: () => [],
        };
      } else if (
        tableMetadata.destinationType === TransferStructuredTableDestinationType.BOX &&
        column.columnType === TableColumnType.POSITION
      ) {
        return {
          customEvalFn: DestinationPositionCustomEval.destinationBoxPositionCustomEval,
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: (columns) => [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.TARGET && columnType === TableColumnType.BOX
            ),
          ],
        };
      } else if (
        tableMetadata.destinationType === TransferStructuredTableDestinationType.MATRIX_PLATE &&
        column.columnType === TableColumnType.POSITION
      ) {
        return {
          customEvalFn: DestinationPositionCustomEval.destinationMatrixPlatePositionCustomEval,
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: (columns) => [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.TARGET && columnType === TableColumnType.PLATE
            ),
          ],
        };
      } else if (column.columnType === TableColumnType.CONTAINERS) {
        if (tableMetadata.barcodingStrategy === TransferStructuredTableBarcodingStrategy.CUSTOM) {
          return {
            customEvalFn: ContainerDestinationTypeCustomEval.destinationCreateContainerCustomEval,
            customEvalType: CustomEvalType.DEFAULT,
            getColumnIndexesOfDependencies: () => [],
          };
        }
      } else if (
        tableMetadata.destinationType === TransferStructuredTableDestinationType.CONTAINER &&
        column.columnType === TableColumnType.PARENT_STORABLE
      ) {
        return {
          customEvalFn: ParentStorableCustomEval.validateParentStorableCapacityCustomEval(),
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: (columns) => [],
        };
      }
      return null;
    } else if (tableMetadata.mode === InventoryStructuredTableMode.FILL) {
      switch (tableMetadata.destinationType) {
        case TransferStructuredTableDestinationType.CONTAINER:
          if (column.columnType === TableColumnType.CONTAINERS) {
            return {
              customEvalFn: ContainerDestinationTypeCustomEval.destinationFillContainerCustomEval,
              customEvalType: CustomEvalType.DEFAULT,
              getColumnIndexesOfDependencies: () => [],
            };
          } else if (column.columnType === TableColumnType.TAG_VALUE) {
            return {
              customEvalFn: SchemaTagsCustomEval.containerSchemaTagCustomEval,
              customEvalType: CustomEvalType.DEFAULT,
              getColumnIndexesOfDependencies: (columns) => [
                columns.findIndex(
                  ({columnGroup, columnType}) =>
                    columnGroup === TransferTableColumnGroup.TARGET &&
                    columnType === TableColumnType.CONTAINERS
                ),
              ],
            };
          }
          return null;
        case TransferStructuredTableDestinationType.PLATE:
          if (column.columnType === TableColumnType.POSITION) {
            return {
              customEvalFn: DestinationPositionCustomEval.destinationPlatePositionCustomEval,
              customEvalType: CustomEvalType.DEFAULT,
              getColumnIndexesOfDependencies: (columns) => [
                columns.findIndex(
                  ({columnGroup, columnType}) =>
                    columnGroup === TransferTableColumnGroup.TARGET && columnType === TableColumnType.PLATE
                ),
              ],
            };
          } else if (column.columnType === TableColumnType.PLATE) {
            return {
              customEvalFn: destinationPlateCustomEval,
              customEvalType: CustomEvalType.DEFAULT,
              getColumnIndexesOfDependencies: () => [],
            };
          } else if (column.columnType === TableColumnType.TAG_VALUE) {
            return {
              customEvalFn: SchemaTagsCustomEval.getPlateWellSchemaTagCustomEval(),
              customEvalType: CustomEvalType.DEFAULT,
              getColumnIndexesOfDependencies: (columns) => [
                columns.findIndex(
                  ({columnGroup, columnType}) =>
                    columnGroup === TransferTableColumnGroup.TARGET && columnType === TableColumnType.PLATE
                ),
                columns.findIndex(
                  ({columnGroup, columnType}) =>
                    columnGroup === TransferTableColumnGroup.TARGET && columnType === TableColumnType.POSITION
                ),
              ],
            };
          }
          return null;
        default:
          return null;
      }
    } else {
      throw new Error('Illegal InventoryStructuredTableMode');
    }
  }

  private getQuantityColumnCustomEvalInfo(
    tableMetadata: TransferStructuredTableMetadata
  ): ColumnCustomEvalInfo | null {
    // The Quantity column needs to be validated against several different sets of source columns,
    // so the customEval definitions are a little complicated.
    switch (tableMetadata.sourceType) {
      case TransferStructuredTableSourceType.BIOENTITY:
        return {
          customEvalFn: TransferQuantityCustomEval.quantityWithSourceEntityCustomEval,
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: () => [],
        };
      case TransferStructuredTableSourceType.CONTAINER:
        return {
          customEvalFn: TransferQuantityCustomEval.quantityWithSourceContainerCustomEval,
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: (columns) => [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.CONTAINERS
            ),
          ],
        };
      case TransferStructuredTableSourceType.PLATE:
        return {
          customEvalFn: TransferQuantityCustomEval.quantityWithSourcePlateCustomEval,
          customEvalType: CustomEvalType.DEFAULT,
          getColumnIndexesOfDependencies: (columns) => [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.PLATE
            ),
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.POSITION
            ),
          ],
        };
      default:
        assertAndContinue(false, `Unexpected source type ${assertNever(tableMetadata.sourceType)}`);
        return null;
    }
  }

  private getSampleControlRolesColumnDependencies({
    columns,
    sourceType,
    getDestinationContainerColumn,
    getSampleOwnersColumn,
    getRestrictionStatusColumn,
  }: {
    columns: ReadonlyArray<StructuredTableColumn>;
    sourceType: TransferStructuredTableSourceType;
    getDestinationContainerColumn?: boolean;
    getSampleOwnersColumn?: boolean;
    getRestrictionStatusColumn?: boolean;
  }): Array<number> {
    const sourceColumns = [];
    switch (sourceType) {
      case TransferStructuredTableSourceType.PLATE:
        sourceColumns.push(
          columns.findIndex(
            ({columnGroup, columnType}) =>
              columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.PLATE
          ),
          columns.findIndex(
            ({columnGroup, columnType}) =>
              columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.POSITION
          )
        );
        break;
      case TransferStructuredTableSourceType.CONTAINER:
        sourceColumns.push(
          columns.findIndex(
            ({columnGroup, columnType}) =>
              columnGroup === TransferTableColumnGroup.SOURCE && columnType === TableColumnType.CONTAINERS
          )
        );
        break;
      case TransferStructuredTableSourceType.BIOENTITY:
        break;
      default:
        assert(false, `Unexpected TransferStructuredTableType ${sourceType}`);
        break;
    }

    return [
      ...sourceColumns,
      ...(getDestinationContainerColumn
        ? [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.TARGET && columnType === TableColumnType.CONTAINERS
            ),
          ]
        : []),
      ...(getSampleOwnersColumn
        ? [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS &&
                columnType === TableColumnType.PARTIES
            ),
          ]
        : []),
      ...(getRestrictionStatusColumn
        ? [
            columns.findIndex(
              ({columnGroup, columnType}) =>
                columnGroup === TransferTableColumnGroup.TARGET &&
                columnType === TableColumnType.SAMPLE_RESTRICTION_STATUS
            ),
          ]
        : []),
    ].filter((index) => index !== -1);
  }

  private getSampleControlRolesCustomEvalInfo(
    tableMetadata: TransferStructuredTableMetadata,
    column: PartiesStructuredTableColumn | SampleRestrictionStatusStructuredTableColumn
  ): ColumnCustomEvalInfo | null {
    const useDecoupledLogic = hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2);
    switch (tableMetadata.mode) {
      case InventoryStructuredTableMode.CREATE: {
        if (
          tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER ||
          tableMetadata.sourceType === TransferStructuredTableSourceType.BIOENTITY
        ) {
          return column.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
            ? {
                customEvalFn: SampleControlRolesCustomEval.getSampleOwnersFromSourceOrDestinationContainer({
                  hasSourceContainer:
                    tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER,
                  hasDestinationContainer: false,
                }),
                customEvalType: CustomEvalType.DEFAULT,
                getColumnIndexesOfDependencies: (columns) =>
                  this.getSampleControlRolesColumnDependencies({
                    columns,
                    sourceType: tableMetadata.sourceType,
                  }),
              }
            : column.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
              ? {
                  customEvalFn:
                    SampleControlRolesCustomEval.getRestrictedSampleUsersFromSourceOrDestinationContainer({
                      hasSourceContainer:
                        tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER,
                      hasDestinationContainer: false,
                    }),
                  customEvalType: CustomEvalType.DEFAULT,
                  getColumnIndexesOfDependencies: (columns) =>
                    this.getSampleControlRolesColumnDependencies({
                      columns,
                      sourceType: tableMetadata.sourceType,
                      getSampleOwnersColumn: !useDecoupledLogic,
                      getRestrictionStatusColumn: useDecoupledLogic,
                    }),
                }
              : column.columnType === TableColumnType.SAMPLE_RESTRICTION_STATUS
                ? {
                    customEvalFn:
                      SampleControlRolesCustomEval.getRestrictionStatusFromSourceOrDestinationContainer({
                        hasSourceContainer:
                          tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER,
                        hasDestinationContainer: false,
                      }),
                    customEvalType: CustomEvalType.DEFAULT,
                    getColumnIndexesOfDependencies: (columns) =>
                      this.getSampleControlRolesColumnDependencies({
                        columns,
                        sourceType: tableMetadata.sourceType,
                      }),
                  }
                : null;
        }
        if (tableMetadata.sourceType === TransferStructuredTableSourceType.PLATE) {
          switch (column.columnGroup) {
            case StructuredTableColumnGroups.SAMPLE_OWNERS:
              return {
                customEvalFn: SampleControlRolesCustomEval.getSampleOwnersFromSourcePlate({
                  hasDestinationContainer: false,
                }),
                customEvalType: CustomEvalType.DEFAULT,
                getColumnIndexesOfDependencies: (columns) =>
                  this.getSampleControlRolesColumnDependencies({
                    columns,
                    sourceType: TransferStructuredTableSourceType.PLATE,
                  }),
              };
            case StructuredTableColumnGroups.AUTHORIZED_USERS:
              return {
                customEvalFn: SampleControlRolesCustomEval.getRestrictedSampleUsersFromSourcePlate({
                  hasDestinationContainer: false,
                }),
                customEvalType: CustomEvalType.DEFAULT,
                getColumnIndexesOfDependencies: (columns) =>
                  this.getSampleControlRolesColumnDependencies({
                    columns,
                    sourceType: TransferStructuredTableSourceType.PLATE,
                    getSampleOwnersColumn: !useDecoupledLogic,
                    getRestrictionStatusColumn: useDecoupledLogic,
                  }),
              };
            case TransferTableColumnGroup.TARGET:
              if (column.columnType === TableColumnType.SAMPLE_RESTRICTION_STATUS) {
                return {
                  customEvalFn: SampleControlRolesCustomEval.getRestrictionStatusFromSourcePlate({
                    hasDestinationContainer: false,
                  }),
                  customEvalType: CustomEvalType.DEFAULT,
                  getColumnIndexesOfDependencies: (columns) =>
                    this.getSampleControlRolesColumnDependencies({
                      columns,
                      sourceType: TransferStructuredTableSourceType.PLATE,
                    }),
                };
              }
              return null;
            default:
              return null;
          }
        }
        return null;
      }
      case InventoryStructuredTableMode.FILL: {
        if (tableMetadata.destinationType === TransferStructuredTableDestinationType.PLATE) {
          return null;
        }
        if (
          tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER ||
          tableMetadata.sourceType === TransferStructuredTableSourceType.BIOENTITY
        ) {
          return column.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS
            ? {
                customEvalFn: SampleControlRolesCustomEval.getSampleOwnersFromSourceOrDestinationContainer({
                  hasSourceContainer:
                    tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER,
                  hasDestinationContainer: true,
                  storableSchemaId: column.frontEndMetadata?.storableSchemaId ?? null,
                }),
                customEvalType: CustomEvalType.DEFAULT,
                getColumnIndexesOfDependencies: (columns) =>
                  this.getSampleControlRolesColumnDependencies({
                    columns,
                    sourceType: tableMetadata.sourceType,
                    getDestinationContainerColumn: true,
                  }),
              }
            : column.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS
              ? {
                  customEvalFn:
                    SampleControlRolesCustomEval.getRestrictedSampleUsersFromSourceOrDestinationContainer({
                      storableSchemaId: column.frontEndMetadata?.storableSchemaId ?? null,
                      hasSourceContainer:
                        tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER,
                      hasDestinationContainer: true,
                    }),
                  customEvalType: CustomEvalType.DEFAULT,
                  getColumnIndexesOfDependencies: (columns) =>
                    this.getSampleControlRolesColumnDependencies({
                      columns,
                      sourceType: tableMetadata.sourceType,
                      getDestinationContainerColumn: true,
                      getSampleOwnersColumn: !useDecoupledLogic,
                      getRestrictionStatusColumn: useDecoupledLogic,
                    }),
                }
              : column.columnType === TableColumnType.SAMPLE_RESTRICTION_STATUS
                ? {
                    customEvalFn:
                      SampleControlRolesCustomEval.getRestrictionStatusFromSourceOrDestinationContainer({
                        hasSourceContainer:
                          tableMetadata.sourceType === TransferStructuredTableSourceType.CONTAINER,
                        hasDestinationContainer: true,
                        storableSchemaId: column.frontEndMetadata?.storableSchemaId ?? null,
                      }),
                    customEvalType: CustomEvalType.DEFAULT,
                    getColumnIndexesOfDependencies: (columns) =>
                      this.getSampleControlRolesColumnDependencies({
                        columns,
                        sourceType: tableMetadata.sourceType,
                        getDestinationContainerColumn: true,
                      }),
                  }
                : null;
        }
        if (tableMetadata.sourceType === TransferStructuredTableSourceType.PLATE) {
          const customEvalFnParams = {
            storableSchemaId: column.frontEndMetadata?.storableSchemaId ?? null,
            hasDestinationContainer: true,
          };

          switch (column.columnGroup) {
            case StructuredTableColumnGroups.SAMPLE_OWNERS:
              return {
                customEvalFn: SampleControlRolesCustomEval.getSampleOwnersFromSourcePlate(customEvalFnParams),
                customEvalType: CustomEvalType.DEFAULT,
                getColumnIndexesOfDependencies: (columns) =>
                  this.getSampleControlRolesColumnDependencies({
                    columns,
                    sourceType: TransferStructuredTableSourceType.PLATE,
                    getDestinationContainerColumn: true,
                  }),
              };
            case StructuredTableColumnGroups.AUTHORIZED_USERS:
              return {
                customEvalFn:
                  SampleControlRolesCustomEval.getRestrictedSampleUsersFromSourcePlate(customEvalFnParams),
                customEvalType: CustomEvalType.DEFAULT,
                getColumnIndexesOfDependencies: (columns) =>
                  this.getSampleControlRolesColumnDependencies({
                    columns,
                    sourceType: TransferStructuredTableSourceType.PLATE,
                    getDestinationContainerColumn: true,
                    getSampleOwnersColumn: !useDecoupledLogic,
                    getRestrictionStatusColumn: useDecoupledLogic,
                  }),
              };
            case TransferTableColumnGroup.TARGET:
              if (column.columnType === TableColumnType.SAMPLE_RESTRICTION_STATUS) {
                return {
                  customEvalFn:
                    SampleControlRolesCustomEval.getRestrictionStatusFromSourcePlate(customEvalFnParams),
                  customEvalType: CustomEvalType.DEFAULT,
                  getColumnIndexesOfDependencies: (columns) =>
                    this.getSampleControlRolesColumnDependencies({
                      columns,
                      sourceType: TransferStructuredTableSourceType.PLATE,
                      getDestinationContainerColumn: true,
                    }),
                };
              }
              return null;
            default:
              return null;
          }
        }
        return null;
      }
      default:
        assertAndContinue(false, `Unexpected table mode ${assertNever(tableMetadata).mode}`);
        return null;
    }
  }

  private getColumnCustomEvalInfo(
    tableMetadata: TransferStructuredTableMetadata,
    column: StructuredTableColumn
  ): ColumnCustomEvalInfo | null {
    if (column.columnGroup === TransferTableColumnGroup.SOURCE) {
      return this.getSourceColumnCustomEvalInfo(tableMetadata, column);
    } else if (
      column.columnGroup === TransferTableColumnGroup.TARGET &&
      ![TableColumnType.QUANTITY, TableColumnType.SAMPLE_RESTRICTION_STATUS].includes(column.columnType)
    ) {
      return this.getDestinationColumnCustomEvalInfo(tableMetadata, column);
    } else if (column.columnType === TableColumnType.QUANTITY) {
      return this.getQuantityColumnCustomEvalInfo(tableMetadata);
    } else if (
      isPartiesStructuredTableColumn(column) ||
      isSampleRestrictionStatusStructuredTableColumn(column)
    ) {
      return this.getSampleControlRolesCustomEvalInfo(tableMetadata, column);
    } else {
      return null;
    }
  }

  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: TransferStructuredTableMetadata;
    rowMetadata: TransferStructuredTableRowMetadata;
  }> | null {
    const {column, row} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const {metadata} = tableView;
    if (row.isFinalized) {
      return null;
    }

    const customEvalInfo = this.getColumnCustomEvalInfo(metadata, column);
    if (!customEvalInfo) {
      return null;
    }
    return {
      customEvalFn: customEvalInfo.customEvalFn,
      customEvalType: customEvalInfo.customEvalType,
    };
  }

  override getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>,
    cellIndex: CellIndex,
    options: GetCustomEvalArgsOptions
  ): Array<string> {
    const {toSpreadsheetIndex} = options;
    const {columns, metadata} = tableView;
    const {row: currentRowIndex, column: currentColumnIndex} = cellIndex;
    const dependsOnColumnIndexes =
      this.getColumnCustomEvalInfo(metadata, columns[currentColumnIndex])?.getColumnIndexesOfDependencies(
        columns
      ) ?? [];

    return dependsOnColumnIndexes.map((dependencyIndex, slotIndex) => {
      assert(
        dependencyIndex !== -1,
        `Could not find dependency at slot ${slotIndex} for column at index ${currentColumnIndex}`
      );
      return `=${toSpreadsheetIndex({row: currentRowIndex, column: dependencyIndex})}`;
    });
  }

  override getSampleInfosForRequest(
    structuredTable: TransferStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): Array<SampleLookupInfoForCreateRequest> {
    const sourceTable = this.toTable(structuredTable);
    const tableColumnDefinitions = sourceTable.columnConstraints.map(
      (constraint) => constraint?.columnDefinition
    );
    const columnGroup = TransferTableColumnGroup.TARGET;

    // Prefer to read cell values from computedTable which has populated values for merge cells
    const finalizedRowIndices = structuredTable.rows
      .map((row, rowIndex) => (row.isFinalized ? rowIndex : -1))
      .filter((rowIndex) => rowIndex !== -1);
    const getTypedValue = (rowIndex: number, columnIndex: number): TypedValue | null =>
      computedTable?.[rowIndex]?.[columnIndex]?.value ??
      sourceTable.table[rowIndex][columnIndex].typedValue ??
      null;

    switch (structuredTable.metadata.destinationType) {
      case TransferStructuredTableDestinationType.CONTAINER:
      case TransferStructuredTableDestinationType.BOX:
      case TransferStructuredTableDestinationType.MATRIX_PLATE: {
        const destinationContainerColumnIndex = getColumnDefinitionIndex({
          columnGroup,
          columnDefinitionType: TableColumnType.CONTAINERS,
          tableColumnDefinitions,
        });
        const samples: Array<SampleDetails> = finalizedRowIndices
          .flatMap((rowIndex) =>
            getSampleDetailsFromCell(
              computedTable?.[rowIndex]?.[destinationContainerColumnIndex] ??
                sourceTable.table[rowIndex][destinationContainerColumnIndex],
              TableColumnType.CONTAINERS
            )
          )
          .filter((sampleDetails): sampleDetails is SampleDetails => sampleDetails != null);

        return compact(samples.map((sampleDetails) => getSampleId(sampleDetails))).map((sampleId) => ({
          idForLookup: sampleId,
        }));
      }
      case TransferStructuredTableDestinationType.PLATE: {
        // Get the plates and their destination positions of the finalized rows
        const destinationPositionColumnIndex = getColumnDefinitionIndex({
          columnGroup,
          columnDefinitionType: TableColumnType.POSITION,
          tableColumnDefinitions,
        });

        const destinationPlateColumnIndex = getColumnDefinitionIndex({
          columnGroup,
          columnDefinitionType: TableColumnType.PLATE,
          tableColumnDefinitions,
        });

        const positions = finalizedRowIndices.map((rowIndex) => {
          const value = getTypedValue(rowIndex, destinationPositionColumnIndex);
          // TODO(BNCH-66692) - Change this back to `assert`.
          if (value?.type !== BaseTypedValueType.STRING) {
            assertAndContinue(false, 'Positions should be string typed values');
            return '';
          }
          return value.rawValue;
        });

        const plateIds = finalizedRowIndices.map((rowIndex) => {
          const value = getTypedValue(rowIndex, destinationPlateColumnIndex);
          // TODO(BNCH-66692) - Change this back to `assert`.
          if (value?.type !== BaseTypedValueType.STORABLES) {
            assertAndContinue(
              false,
              'Cells of Destination Plate column in finalized rows should contain plates'
            );
            return '';
          }
          return value.storableSummaries[0].id;
        });

        return plateIds.map((plateId, idx) => ({
          idForLookup: plateId,
          plateWellPositionString: positions[idx],
        }));
      }

      default:
        throw new Error('Invalid destination type for transfer table');
    }
  }

  /**
   * Return validation functions that check across rows.
   * Checks for:
   *   - duplicate barcodes if the TransferStructuredTableBarcodingStrategy is CUSTOM, in CREATE mode.
   *   - duplicate existing destinations to fill
   *   - insufficient parent storable capacity for all items being created in the parent
   */
  override getTableValidationFns(): Array<
    TableValidationFn<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>
  > {
    return [
      ...super.getTableValidationFns(),
      (structuredTable: TransferStructuredTable) => {
        const sourceTable = this.toTable(structuredTable);
        return getErrorsForDuplicateBarcodesInNonFinalizedRows({structuredTable, sourceTable});
      },
      (structuredTable: TransferStructuredTable, computedTable: Array<Array<EvaluatedCell>>) => {
        const sourceTable = this.toTable(structuredTable);
        return getErrorsForDuplicatePositionsInNonFinalizedRows({
          structuredTable,
          computedTable,
          sourceTable,
        });
      },
      (structuredTable: TransferStructuredTable, computedTable: Array<Array<EvaluatedCell>>) => {
        const sourceTable = this.toTable(structuredTable);
        return getErrorsForDuplicateDestinationChipsInNonFinalizedRows({
          structuredTable,
          computedTable,
          sourceTable,
        });
      },
      (structuredTable: TransferStructuredTable, computedTable: Array<Array<EvaluatedCell>>) => {
        const {parentStorablesByRowId, parentStorableColumnIndex} = this.getParentStorableSampleByRowId(
          structuredTable,
          computedTable
        );
        return getErrorsForInvalidParentStorableCapacityInNonFinalizedRows({
          structuredTable,
          computedTable,
          parentStorablesByRowId,
          parentStorableColumnIndex,
        });
      },
    ];
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: TransferStructuredTable,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<StructuredTableAddOperation<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>> {
    const ops: Array<
      StructuredTableAddOperation<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>
    > = [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
        onSelect: ({note, structuredTable, addSamples}) => {
          addSamplesFromStructuredTable({
            destinationTable: structuredTable,
            adapter: this,
            note,
            entryId,
            addSamples,
          });
        },
      },
    ];

    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(structuredTable, this, addSamples, WORKLIST_FILL_LOCATION.TRANSFER_TABLE);
        },
      });
    }

    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
      onSelect: async ({structuredTable, addFullRows}) => {
        const containerSchema = structuredTable.schemas[0] as ContainerSchema;
        assert(
          containerSchema != null,
          'Tried to import spreadsheet into transfer table with inaccessible or nonexistent container schema'
        );

        const rows = await importRowsFromSpreadsheet({
          structuredTable,
          schemas: {containerSchema},
          getSpreadsheetTypeForColumn: (column) => this.getSpreadsheetTypeForColumn(column),
        });

        if (rows) {
          addFullRows(rows);
        }
      },
    });

    if (BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_USING_AI,
        onSelect: ({structuredTable}) => {
          addAIDataEntryRows?.(undefined, structuredTable.name);
        },
      });
    }
    return ops;
  }

  private getSpreadsheetTypeForColumn(
    column: StructuredTableColumn
  ): SpreadsheetBulkImportConstants.COLUMN_TYPE | null {
    if (column.columnGroup === TransferTableColumnGroup.SOURCE) {
      switch (column.columnType) {
        case TableColumnType.EXISTING_BIOENTITY:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_BIOENTITY_LABEL;
        case TableColumnType.CONTAINERS:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_CONTAINER_BARCODE;
        case TableColumnType.PLATE:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_GRID_BARCODE;
        case TableColumnType.POSITION:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_GRID_COORDINATES;
        default:
          assertAndContinue(false, `Unsupported column type ${column.columnType}`);
          return null;
      }
    } else if (column.columnGroup === TransferTableColumnGroup.TARGET) {
      switch (column.columnType) {
        case TableColumnType.PARENT_STORABLE:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.PARENT_STORABLE_BARCODE;
        case TableColumnType.CONTAINERS:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE;
        case TableColumnType.BOX:
        case TableColumnType.PLATE:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.OUTPUT_GRID_BARCODE;
        case TableColumnType.POSITION:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.OUTPUT_GRID_COORDINATES;
        case TableColumnType.QUANTITY:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.TRANSFER_QUANTITY;
        case TableColumnType.CONCENTRATION:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_CONCENTRATION_VALUE;
        case TableColumnType.TAG_VALUE:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.CONTAINER_SCHEMA_FIELD;
        case TableColumnType.MISSING_FIELD:
          return null;
        case TableColumnType.SAMPLE_RESTRICTION_STATUS:
          return SpreadsheetBulkImportConstants.COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS;
        case TableColumnType.STUDIES:
          return null;
        default:
          assertAndContinue(false, `Unsupported column type ${column.columnType}`);
          return null;
      }
    } else if (column.columnGroup === StructuredTableColumnGroups.SAMPLE_OWNERS) {
      return column.columnType === TableColumnType.PARTIES
        ? SpreadsheetBulkImportConstants.COLUMN_TYPE.SAMPLE_OWNERS
        : null;
    } else if (column.columnGroup === StructuredTableColumnGroups.AUTHORIZED_USERS) {
      return column.columnType === TableColumnType.PARTIES
        ? SpreadsheetBulkImportConstants.COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS
        : null;
    } else {
      if ([TableColumnType.DEDICATED_LOOKUP, TableColumnType.CUSTOM].includes(column.columnType)) {
        return null;
      }
      throw new Error(`Unexpected column group ${column.columnGroup}`);
    }
  }

  override getColumnSummariesForCreateProcessShortcut(
    structuredTable: TransferStructuredTable
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    const containerDestinationColumnRequirements = [
      {
        columnType: TableColumnType.TAG_VALUE,
        schemaFieldType: FieldType.STORABLE_LINK,
        folderItemType: null,
      },
      {columnType: TableColumnType.CONTAINERS, schemaFieldType: null, folderItemType: null},
    ];
    if (structuredTable.metadata.destinationType === TransferStructuredTableDestinationType.PLATE) {
      const destinationPlateColumnIndex = structuredTable.columns.findIndex(
        (column) =>
          column.columnGroup === TransferTableColumnGroup.TARGET &&
          column.columnType === TableColumnType.PLATE
      );
      assert(
        destinationPlateColumnIndex !== -1,
        'Found transfer table configured with a PLATE destination type without a destination plate column'
      );
      return [
        // Destination well column
        {
          // For column mapping purposes, mapping from the destination well should still use the destination plate as the primary item,
          // so we use the same column index for both.
          columnIndex: destinationPlateColumnIndex,
          validDestinationColumnRequirements: containerDestinationColumnRequirements,
          schemaIdConstraint: null,
          shouldFetchPlateInfo: true,
        },
        // Destination plate column
        {
          columnIndex: destinationPlateColumnIndex,
          validDestinationColumnRequirements: containerDestinationColumnRequirements,
          schemaIdConstraint: null,
          shouldFetchPlateInfo: false,
        },
      ];
    } else {
      // The primary column is the destination container
      const destinationContainerColumnIndex = structuredTable.columns.findIndex(
        (column) =>
          column.columnGroup === TransferTableColumnGroup.TARGET &&
          column.columnType === TableColumnType.CONTAINERS
      );
      assert(
        destinationContainerColumnIndex !== -1,
        `Found transfer table configured with a ${structuredTable.metadata.destinationType} destination type without a destination container column`
      );
      return [
        {
          validDestinationColumnRequirements: containerDestinationColumnRequirements,
          schemaIdConstraint: structuredTable.schemas[0]?.id ?? null,
          columnIndex: destinationContainerColumnIndex,
        },
      ];
    }
  }

  override isReadOnly(): boolean {
    return !isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT});
  }

  /**
   * Returns column indices for source primary samples present in a transfer table.
   *
   * Note that the column index will be -1 for samples that aren't present in the given table.
   */
  static findSourcePrimarySampleColumnIndices(
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>
  ): {
    sourceEntityColumnIndex: number;
    sourceContainerColumnIndex: number;
    sourcePlateColumnIndex: number;
    sourcePositionColumnIndex: number;
  } {
    let sourceEntityColumnIndex = -1;
    let sourceContainerColumnIndex = -1;
    let sourcePlateColumnIndex = -1;
    let sourcePositionColumnIndex = -1;

    for (const column of structuredTable.columns.filter(
      (column) => column.columnGroup === TransferTableColumnGroup.SOURCE
    )) {
      const colIdx = structuredTable.columns.findIndex((col) => col === column);
      if (column.columnType === TableColumnType.EXISTING_BIOENTITY) {
        sourceEntityColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.CONTAINERS) {
        sourceContainerColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.PLATE) {
        sourcePlateColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.POSITION) {
        sourcePositionColumnIndex = colIdx;
      }
    }

    return {
      sourceEntityColumnIndex,
      sourceContainerColumnIndex,
      sourcePlateColumnIndex,
      sourcePositionColumnIndex,
    };
  }

  /**
   * Returns column indices for target primary samples present in a transfer table.
   *
   * Note that the column index will be -1 for samples that aren't present in the given table.
   */
  private findTargetPrimarySampleColumnIndices(
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>
  ): {
    targetContainerColumnIndex: number;
    targetPlateColumnIndex: number;
    targetPositionColumnIndex: number;
    targetLocationColumnIndex: number;
    targetBoxColumnIndex: number;
  } {
    let targetContainerColumnIndex = -1;
    let targetPlateColumnIndex = -1;
    let targetPositionColumnIndex = -1;
    let targetLocationColumnIndex = -1;
    let targetBoxColumnIndex = -1;

    for (const column of structuredTable.columns.filter(
      (column) => column.columnGroup === TransferTableColumnGroup.TARGET
    )) {
      const colIdx = structuredTable.columns.findIndex((col) => col === column);
      if (column.columnType === TableColumnType.CONTAINERS) {
        targetContainerColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.PLATE) {
        targetPlateColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.POSITION) {
        targetPositionColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.BOX) {
        targetBoxColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.PARENT_STORABLE) {
        targetLocationColumnIndex = colIdx;
      }
    }

    return {
      targetContainerColumnIndex,
      targetPlateColumnIndex,
      targetPositionColumnIndex,
      targetBoxColumnIndex,
      targetLocationColumnIndex,
    };
  }

  // Keep in sync with TransferInstantiator._get_finalized_source_sample_ids in
  // benchling/notebook/structured_tables/instantiators/transfer_table_instantiator.py
  private getSourcePrimarySamples(tableInfoForSearch: {
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>;
    computedTable?: Array<Array<EvaluatedCell>>;
    rowIndex: number;
  }): Array<PrimarySample> {
    const {structuredTable, computedTable, rowIndex} = tableInfoForSearch;
    const {
      sourceEntityColumnIndex,
      sourceContainerColumnIndex,
      sourcePlateColumnIndex,
      sourcePositionColumnIndex,
    } = TransferStructuredTableAdapter.findSourcePrimarySampleColumnIndices(structuredTable);
    const sourcePrimarySamples: Array<PrimarySample> = [];
    const {sourceType} = structuredTable.metadata;
    const cells = structuredTable.rows[rowIndex].cells;
    const columnGroup = TransferTableColumnGroup.SOURCE;
    // The transfer table always has a source bioentity present regardless of source type, so we extract it as a primary value outside the switch statement below.
    const bioentityTypedValue =
      computedTable?.[rowIndex][sourceEntityColumnIndex]?.value ?? cells[sourceEntityColumnIndex].typedValue;
    const bioentityInfo = bioentityTypedValue ? getBioentityInfoFromTypedValue(bioentityTypedValue) : null;
    if (bioentityInfo) {
      sourcePrimarySamples.push({
        schemaId: null,
        columnGroup: TransferTableColumnGroup.SOURCE,
        sampleLookupInfo: {
          sampleType: PrimarySampleLookupType.DIRECT,
          idForLookup: bioentityInfo.id,
        },
      });
    }
    switch (sourceType) {
      case TransferStructuredTableSourceType.CONTAINER: {
        const typedValue =
          computedTable?.[rowIndex][sourceContainerColumnIndex]?.value ??
          cells[sourceContainerColumnIndex].typedValue;
        const storableInfo = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
        // TODO(BNCH-66692) - Change this back to `assert`.
        assertAndContinue(
          storableInfo != null,
          'Found null storable value in source container column transfer table'
        );

        sourcePrimarySamples.push({
          schemaId: null,
          columnGroup,
          sampleLookupInfo:
            storableInfo != null
              ? {
                  sampleType: PrimarySampleLookupType.DIRECT,
                  idForLookup: storableInfo.id,
                }
              : null,
        });

        break;
      }
      case TransferStructuredTableSourceType.PLATE: {
        const typedValue =
          computedTable?.[rowIndex][sourcePlateColumnIndex]?.value ??
          cells[sourcePlateColumnIndex].typedValue;
        const positionTypedValue =
          computedTable?.[rowIndex][sourcePositionColumnIndex]?.value ??
          cells[sourcePositionColumnIndex].typedValue;
        // TODO(BNCH-66692) - Change this back to `assert`.
        assertAndContinue(
          positionTypedValue != null,
          'Found null storable value in source plate position column transfer table'
        );
        const storableInfo = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
        // TODO(BNCH-66692) - Change this back to `assert`.
        assertAndContinue(
          storableInfo != null,
          'Found null storable value in source plate column transfer table'
        );
        sourcePrimarySamples.push({
          schemaId: null,
          columnGroup,
          sampleLookupInfo:
            storableInfo && positionTypedValue
              ? {
                  sampleType: PrimarySampleLookupType.PLATE_POSITION_DUPLE,
                  idForLookup: storableInfo.id,
                  plateWellPosition: formatTypedValue(positionTypedValue),
                }
              : null,
        });

        break;
      }
      case TransferStructuredTableSourceType.BIOENTITY:
        // The source bioentity primary sample is always present in the transfer table regardless of source type so we handle it above, outside of the switch statement.
        break;

      default:
        throw new Error(`Found invalid source type on transfer table ${assertNever(sourceType)}`);
    }
    return sourcePrimarySamples;
  }

  // Keep in sync with TransferInstantiator._get_finalized_target_sample_ids in
  // benchling/notebook/structured_tables/instantiators/transfer_table_instantiator.py
  private getTargetPrimarySamples(tableInfoForSearch: {
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>;
    computedTable?: Array<Array<EvaluatedCell>>;
    rowIndex: number;
  }): Array<PrimarySample> {
    const {structuredTable, computedTable, rowIndex} = tableInfoForSearch;
    const {
      targetContainerColumnIndex,
      targetPlateColumnIndex,
      targetPositionColumnIndex,
      targetBoxColumnIndex,
      targetLocationColumnIndex,
    } = this.findTargetPrimarySampleColumnIndices(structuredTable);
    const targetPrimarySamples: Array<PrimarySample> = [];
    const {mode, destinationType} = structuredTable.metadata;
    const schema = structuredTable.schemas[0];
    const cells = structuredTable.rows[rowIndex].cells;
    const columnGroup = TransferTableColumnGroup.TARGET;
    switch (destinationType) {
      case TransferStructuredTableDestinationType.CONTAINER:
      case TransferStructuredTableDestinationType.BOX:
      case TransferStructuredTableDestinationType.MATRIX_PLATE: {
        // Add parent storable primary sample if mode is create.
        if (mode === InventoryStructuredTableMode.CREATE) {
          const parentStorableColumnIndex = Math.max(
            targetBoxColumnIndex,
            targetPlateColumnIndex,
            targetLocationColumnIndex
          );
          const parentStorableTypedValue =
            computedTable != null
              ? computedTable[rowIndex][parentStorableColumnIndex]?.value
              : cells[parentStorableColumnIndex].typedValue;
          const parentStorableInfo = parentStorableTypedValue
            ? getStorableInfoFromTypedValue(parentStorableTypedValue)
            : null;
          // TODO(BNCH-66692) - Change this back to `assert`.
          if (parentStorableInfo == null) {
            assertAndContinue(false, 'Found null parent storable in transfer table');
            return targetPrimarySamples;
          }
          targetPrimarySamples.push({
            schemaId: schema.id,
            columnGroup,
            sampleLookupInfo: {
              sampleType: PrimarySampleLookupType.DIRECT,
              idForLookup: parentStorableInfo.id,
            },
          });
        }
        // Add container parent storable
        const typedValue =
          computedTable != null
            ? computedTable[rowIndex][targetContainerColumnIndex]?.value
            : cells[targetContainerColumnIndex].typedValue;
        const storableInfo = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
        // TODO(BNCH-66692) - Change this back to `assert`.
        if (storableInfo == null) {
          assertAndContinue(
            false,
            'Found null storable value in destination container column transfer table'
          );
          return targetPrimarySamples;
        }
        targetPrimarySamples.push({
          schemaId: schema.id,
          columnGroup,
          sampleLookupInfo: {
            sampleType: PrimarySampleLookupType.DIRECT,
            idForLookup: storableInfo.id,
          },
        });
        break;
      }
      case TransferStructuredTableDestinationType.PLATE: {
        const typedValue =
          computedTable != null
            ? computedTable[rowIndex][targetPlateColumnIndex]?.value
            : cells[targetPlateColumnIndex].typedValue;
        const positionTypedValue =
          computedTable != null
            ? computedTable[rowIndex][targetPositionColumnIndex]?.value
            : cells[targetPositionColumnIndex].typedValue;
        // TODO(BNCH-66692) - Change this back to `assert`.
        if (positionTypedValue == null) {
          assertAndContinue(false, 'Found null storable value in destination position column transfer table');
          return targetPrimarySamples;
        }
        const storableInfo = typedValue ? getStorableInfoFromTypedValue(typedValue) : null;
        // TODO(BNCH-66692) - Change this back to `assert`.
        if (storableInfo == null) {
          assertAndContinue(false, 'Found null storable value in destination plate column transfer table');
          return targetPrimarySamples;
        }
        const positionString = formatTypedValue(positionTypedValue);
        const plateSchema = structuredTable.schemas.find((schema): schema is PlateSchema =>
          APIUtil.isPlateSchema(schema.id)
        );
        // TODO(BNCH-66692) - Change this back to `assert`.
        if (plateSchema == null) {
          assertAndContinue(false, 'Transfer table with destination plate did not contain plate schema');
          return targetPrimarySamples;
        }
        let plateRowIndex;
        let plateColIndex;
        try {
          ({rowIndex: plateRowIndex, columnIndex: plateColIndex} = parseGridPosition({
            position: positionString,
            gridHeight: plateSchema.height,
            gridWidth: plateSchema.width,
          }));
        } catch (e) {
          if (e instanceof InvalidPositionError) {
            assertAndContinue(false, 'Found an invalid position in a submitted transfer table row');
            notifications.error('Found an invalid position in a submitted transfer table row.');
            return targetPrimarySamples;
          } else {
            throw e;
          }
        }
        const wellSchema = structuredTable.schemas.find((schema): schema is ContainerSchema =>
          APIUtil.isContainerSchema(schema.id)
        );
        assertAndContinue(
          wellSchema != null,
          "Found transfer table with destination container that's missing a destination container schema"
        );
        targetPrimarySamples.push({
          schemaId: wellSchema?.id ?? null,
          columnGroup,
          sampleLookupInfo: {
            sampleType: PrimarySampleLookupType.PLATE_POSITION_DUPLE,
            idForLookup: storableInfo.id,
            plateWellPosition: {rowIndex: plateRowIndex, columnIndex: plateColIndex},
          },
        });

        break;
      }
      default:
        throw new Error(`Found invalid destination type on transfer table ${assertNever(destinationType)}`);
    }
    return targetPrimarySamples;
  }

  // Keep in sync with TransferInstantiator._get_parent_storable_column_index in
  // benchling/notebook/structured_tables/instantiators/transfer_table_instantiator.py
  private getParentStorableColumnIndex(
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>
  ): number {
    const {targetPlateColumnIndex, targetBoxColumnIndex, targetLocationColumnIndex} =
      this.findTargetPrimarySampleColumnIndices(structuredTable);
    // We expect only one of these columns to actually be present, and the other indexes to be -1.
    return Math.max(targetBoxColumnIndex, targetPlateColumnIndex, targetLocationColumnIndex);
  }

  private getParentStorableSampleByRowId(
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>>
  ): {parentStorablesByRowId: {[rowId: string]: string}; parentStorableColumnIndex: number} {
    const parentStorableColumnIndex = this.getParentStorableColumnIndex(structuredTable);

    return getParentStorablesByRowId({
      structuredTable,
      computedTable,
      overrideParentColumnIndex: parentStorableColumnIndex,
    });
  }

  // Keep in sync with TransferInstantiator.get_primary_sample_ids_for_row and
  // TransferInstantiator.get_primary_samples_for_row in
  // benchling/notebook/structured_tables/instantiators/transfer_table_instantiator.py. The latter is the
  // column-group-aware variant used by the backend resync action function -- it tags each sample with its
  // SOURCE/TARGET columnGroup exactly as this method does. (Tested by TestTransferInstantiatorPrimarySamples
  // and TestTransferInstantiatorPrimarySampleColumnGroups in
  // tests/unit/notebook/structured_tables/instantiators/transfer_table_instantiator_test.py.)
  override getPrimarySamples(
    structuredTable: StructuredTable<TransferStructuredTableMetadata, TransferStructuredTableRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>>
  ): {[rowId: string]: Array<PrimarySample>} {
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};
    const schema = structuredTable.schemas[0];
    const parentStorableColumnIndex = this.getParentStorableColumnIndex(structuredTable);

    if (schema == null) {
      // Most likely, the user doesn't have permissions on the schema
      for (const {rowId} of structuredTable.rows) {
        primarySamplesByRowId[rowId] = [];
      }
      return primarySamplesByRowId;
    }

    structuredTable.rows.forEach(({rowId, isFinalized}, rowIndex) => {
      primarySamplesByRowId[rowId] = [];
      // If row is not finalized, only fetch parent storable if present. This is used to refresh the queries
      // for total & occupied capacity that power the cross-row capacity validation
      if (!isFinalized) {
        primarySamplesByRowId[rowId] = getUnfinalizedParentStorablePrimarySample({
          structuredTable,
          computedTable,
          rowIndex,
          overrideParentColumnIndex: parentStorableColumnIndex,
        });
        return;
      }
      primarySamplesByRowId[rowId] = [
        ...this.getSourcePrimarySamples({structuredTable, computedTable, rowIndex}),
        ...this.getTargetPrimarySamples({structuredTable, computedTable, rowIndex}),
      ];
    });
    return primarySamplesByRowId;
  }

  override getTableKindRowUpdater(): typeof TransferStructuredTableRowUpdater {
    return TransferStructuredTableRowUpdater;
  }

  override allowMergedCellRangeInColumn(
    structuredTable: TransferStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    return !(
      column.columnGroup === TransferTableColumnGroup.TARGET &&
      [TableColumnType.POSITION, TableColumnType.CONTAINERS].includes(column.columnType)
    );
  }

  override getDomainGraphTypes(
    schemas: Array<SchemaAndColumnGroup>,
    metadata: TransferStructuredTableMetadata
  ): Array<StructuredTableDomainGraphType> {
    switch (metadata.destinationType) {
      case TransferStructuredTableDestinationType.CONTAINER:
        return schemas.map((schema) => ({
          objectSpec: {objectType: DomainGraphType.CONTAINER, attributes: {isFixedPlateWell: false}},
          columnType: TableColumnType.CONTAINERS,
          columnGroup: schema.columnGroup,
        }));
      case TransferStructuredTableDestinationType.PLATE:
        return schemas.map((schema) => ({
          objectSpec: {objectType: DomainGraphType.CONTAINER, attributes: {isFixedPlateWell: true}},
          // It's not possible to select a plate well based on the position column alone - it's also
          // necessary to select a plate in the plate column.
          // This is a placeholder for now - for core object type specific columns to depend on this
          // for custom eval args, columnType may need to be refactored to an array.
          columnType: TableColumnType.POSITION,
          columnGroup: schema.columnGroup,
        }));
      default:
        // TODO: Core objects exist for all destination types. The studies column only has end-to-end support
        // for container and plate destination types, and additional work would be needed to support the others.
        // If other core object type columns are added that need to support all destination types,
        // the studies column may need to be temporarily excluded until full support is implemented.
        return [];
    }
  }

  override enableCustomColumns(structuredTable: TransferStructuredTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: TransferStructuredTable): boolean {
    return true;
  }
}
